import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import type { Item } from '../../core/domain/models'

export interface UseListItemsStateParams {
  /** Server-authoritative items from useList */
  serverItems: Item[]
  /** Current deferred flow ID (empty string when not in flow mode) */
  deferredFlowId: string
  /** Current local flow item IDs (used for scroll-to-new-item in flow mode) */
  localFlowItems: string[]
}

export interface UseListItemsStateResult {
  /** Local items state with optimistic mutations applied */
  itemsState: Item[]
  setItemsState: React.Dispatch<React.SetStateAction<Item[]>>
  /** Active (non-completed) items sorted by order */
  activeItems: Item[]
  /** Completed items sorted by most-recently-updated */
  completedItems: Item[]
  /** Lookup map of active items by ID */
  activeItemsById: Map<string, Item>
  /** Set of item IDs currently animating to completed */
  completingIds: Set<string>
  /** ID of the most recently added item (for highlight animation) */
  highlightedItemId: string | null
  /** Ref to set the next item to scroll to after add */
  lastAddedItemIdRef: React.MutableRefObject<string | null>
  /** Ref tracking locally-deleted item IDs not yet confirmed by server */
  pendingDeletesRef: React.MutableRefObject<Set<string>>
  /** Ref tracking optimistic order values during reorder */
  pendingReordersRef: React.MutableRefObject<Map<string, number>>
  /** Start the completion animation for an item */
  completeItemWithAnimation: (itemId: string) => void
  /** Immediately restore an item from completed state */
  restoreItemImmediate: (itemId: string) => void
  /** Cancel a running completion animation */
  cancelCompletionAnimation: (itemId: string) => void
}

export function useListItemsState(params: UseListItemsStateParams): UseListItemsStateResult {
  const { serverItems, deferredFlowId, localFlowItems } = params

  const [itemsState, setItemsState] = useState<Item[]>([])
  const [completingIds, setCompletingIds] = useState<Set<string>>(new Set())
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null)

  const pendingDeletesRef = useRef(new Set<string>())
  const pendingReordersRef = useRef<Map<string, number>>(new Map())
  const completionTimersRef = useRef(new Map<string, number>())
  const lastAddedItemIdRef = useRef<string | null>(null)
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // --- Completion animation helpers ---

  const cancelCompletionAnimation = useCallback((itemId: string) => {
    const timerId = completionTimersRef.current.get(itemId)
    if (timerId) {
      window.clearTimeout(timerId)
      completionTimersRef.current.delete(itemId)
    }

    setCompletingIds(prev => {
      if (!prev.has(itemId)) return prev
      const next = new Set(prev)
      next.delete(itemId)
      return next
    })
  }, [])

  const restoreItemImmediate = useCallback((itemId: string) => {
    cancelCompletionAnimation(itemId)
    const now = new Date().toISOString()
    setItemsState(prev => prev.map(i => (i.id === itemId ? { ...i, isCompleted: false, updatedAt: now } : i)))
  }, [cancelCompletionAnimation])

  const completeItemWithAnimation = useCallback((itemId: string) => {
    setCompletingIds(prev => {
      const next = new Set(prev)
      next.add(itemId)
      return next
    })

    const existingTimer = completionTimersRef.current.get(itemId)
    if (existingTimer) window.clearTimeout(existingTimer)

    const timerId = window.setTimeout(() => {
      const now = new Date().toISOString()
      setItemsState(prev => prev.map(i => (i.id === itemId ? { ...i, isCompleted: true, updatedAt: now } : i)))
      setCompletingIds(prev => {
        const next = new Set(prev)
        next.delete(itemId)
        return next
      })
      completionTimersRef.current.delete(itemId)
    }, 180)

    completionTimersRef.current.set(itemId, timerId)
  }, [])

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      for (const timerId of completionTimersRef.current.values()) {
        window.clearTimeout(timerId)
      }
      completionTimersRef.current.clear()
    }
  }, [])

  // --- Sync server items → local state ---

  useEffect(() => {
    // Clean up pending deletes that have been confirmed by server (gone from items)
    const currentIds = new Set(serverItems.map(i => i.id))
    for (const id of pendingDeletesRef.current) {
      if (!currentIds.has(id)) {
        pendingDeletesRef.current.delete(id)
      }
    }

    // Filter out any locally deleted items that server might still be sending back temporarily
    const cleanItemsFromServer = serverItems.filter(i => !pendingDeletesRef.current.has(i.id))

    // While an item is animating to completed, keep it in the active list so the transition is visible.
    let merged = cleanItemsFromServer.map(i =>
      completingIds.has(i.id) ? { ...i, isCompleted: false } : i
    )

    // If a completing item disappeared from the server payload briefly, preserve it from local state.
    for (const id of completingIds) {
      if (!merged.some(i => i.id === id)) {
        const existing = itemsState.find(i => i.id === id)
        if (existing) merged.push({ ...existing, isCompleted: false })
      }
    }

    // Preserve optimistic order during pending reorder operations
    // This prevents snap-back while waiting for server confirmation
    if (pendingReordersRef.current.size > 0) {
      merged = merged.map(i => {
        const pendingOrder = pendingReordersRef.current.get(i.id)
        return pendingOrder != null ? { ...i, order: pendingOrder } : i
      })
      // Sort by order to maintain visual consistency
      merged.sort((a, b) => {
        if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1
        return (a.order ?? 0) - (b.order ?? 0)
      })
    }

    // Only update state if items actually changed (#2: prevent redundant renders)
    setItemsState(prev => {
      if (prev.length !== merged.length) return merged
      const changed = merged.some((item, i) =>
        prev[i]?.id !== item.id ||
        prev[i]?.isCompleted !== item.isCompleted ||
        prev[i]?.order !== item.order ||
        prev[i]?.name !== item.name ||
        prev[i]?.quantity !== item.quantity ||
        prev[i]?.updatedAt !== item.updatedAt
      )
      return changed ? merged : prev
    })
  }, [serverItems, completingIds])

  // --- Scroll to newly added item ---

  useEffect(() => {
    const newItemId = lastAddedItemIdRef.current
    if (!newItemId) return

    // Check if this item exists in the current itemsState (which is what gets rendered)
    const itemExists = itemsState.some(i => i.id === newItemId)
    if (!itemExists) return

    // In flow mode the item is rendered via localFlowItems — wait until it appears there too.
    if (deferredFlowId && !localFlowItems.includes(newItemId)) return

    // Clear the ref so we only scroll once
    lastAddedItemIdRef.current = null

    // Highlight the new item briefly
    setHighlightedItemId(newItemId)
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    highlightTimerRef.current = setTimeout(() => setHighlightedItemId(null), 2000)

    // Double rAF ensures the browser has painted the new element before we scroll.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const itemElement = document.querySelector(`[data-item-id="${newItemId}"]`)
        if (itemElement) {
          itemElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      })
    })
  }, [itemsState, localFlowItems, deferredFlowId])

  // --- Derived data ---

  const activeItems = useMemo(() => itemsState.filter(item => !item.isCompleted), [itemsState])
  const completedItems = useMemo(() => itemsState
    .filter(item => item.isCompleted)
    .slice()
    .sort((a, b) => String(b.updatedAt ?? '').localeCompare(String(a.updatedAt ?? ''))), [itemsState])

  const activeItemsById = useMemo(() => {
    const map = new Map<string, Item>()
    for (const item of activeItems) {
      map.set(item.id, item)
    }
    return map
  }, [activeItems])

  return {
    itemsState,
    setItemsState,
    activeItems,
    completedItems,
    activeItemsById,
    completingIds,
    highlightedItemId,
    lastAddedItemIdRef,
    pendingDeletesRef,
    pendingReordersRef,
    completeItemWithAnimation,
    restoreItemImmediate,
    cancelCompletionAnimation,
  }
}
